// ============================================================================
// WIP REPORT — DRILL-DOWN VERSION
// ============================================================================
// WIP = SUM(logged hours × employee standard rate at time of entry)
// Source: prima.tbl_timesheet_entry, filtered to chargeable entries with an
// engagement reference. Drives the same drill engine as the Employee Activity
// Report, with two extra forced filters:
//   - chargeTypeKey = 'C'        (chargeable only)
//   - requireEngagement = true   (entries must reference an engagement)
//
// Drill dimensions available (no fixed hierarchy — user picks order):
//   Client, EngagementType, Engagement, Activity, Employee, WorkDate, Entry
//
// Side filters: Date range, Manager (default = logged-in employee, options
// limited to self + direct sub-managers), Partner, Year, Only Open
// (default ON — closed engagements hidden by default).

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import { SyutDrillScreen } from 'components/ui/SyutDrill';
import type { FilterDisplayItem } from 'components/ui/SyutDrill';
import type { DrillDimension } from 'contexts/DrillContext';
import { SyutEmployeeSelect, SyutScopedEmployeeSelect } from 'components/ui/SyutEntitySelect';
import { useEmployees } from 'hooks/api/useEmployee';
import { useAuthContext } from 'contexts/AuthContext';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fyStart(): string {
  // Default From = April 1 of current FY (India). Configurable later via SYSTEM_DEFAULTS.financial_year_start_month.
  const now = new Date();
  const fyYear = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  return `${fyYear}-04-01`;
}
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDateRange(from?: string, to?: string): string {
  if (!from && !to) return 'All';
  const fmt = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(d).padStart(2, '0')} ${months[m - 1]} ${y}`;
  };
  if (from && to) return `${fmt(from)} – ${fmt(to)}`;
  if (from) return `From ${fmt(from)}`;
  return `Until ${fmt(to!)}`;
}

// ---------------------------------------------------------------------------
// Drill dimensions (no rigid hierarchy — user picks the path)
// ---------------------------------------------------------------------------

const ALL_DIMENSIONS: DrillDimension[] = [
  'Client', 'EngagementType', 'Engagement', 'Activity', 'Employee', 'WorkDate', 'Entry',
];

const DEFAULT_DRILL_PATH: DrillDimension[] = [
  'Client', 'Engagement', 'Activity', 'Employee', 'Entry',
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface WipReportScreenProps {
  /** Module-action codes granted to this user for the wip-report module. Defaults to []. */
  allowedActions?: string[];
}

const WipReportScreen: React.FC<WipReportScreenProps> = ({ allowedActions = [] }) => {
  const { user } = useAuthContext();

  // --- Permission gating ---
  // Drives the DEFAULT selection only. The Manager dropdown is loaded
  // server-side via SyutScopedEmployeeSelect (view ladder) on the wip-report
  // module, so the option list is whatever the backend authorizes.
  const canViewAll = allowedActions.includes('view-all-employees');

  // --- Side-panel filter state ---
  const [fromDate, setFromDate] = useState(fyStart());
  const [toDate, setToDate] = useState(today());
  const [managerId, setManagerId] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [year, setYear] = useState<string>('');
  const [onlyOpen, setOnlyOpen] = useState<boolean>(true); // default ON per design
  const [filterOpen, setFilterOpen] = useState(false);

  // Resolve the logged-in user → their employee row, used purely as the
  // default for the Manager filter when the caller is not allowed to "view all".
  const { data: selfEmployees } = useEmployees({ pageSize: 1, userId: user?.userId });
  const currentEmployee = useMemo(() => selfEmployees[0] || null, [selfEmployees]);

  // Default the Manager filter:
  //   canViewAll → '' (means "All managers" — backend treats the absence of
  //                managerId as no filter on top of view scope)
  //   otherwise  → the logged-in employee's id (self + their team is the
  //                widest set the backend will return anyway)
  useEffect(() => {
    if (managerId) return;
    if (canViewAll) return;
    if (currentEmployee?.id) setManagerId(currentEmployee.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmployee?.id, canViewAll]);

  // Seed option for the controlled SyutSelect — keeps the auto-defaulted self id
  // visible while the async list loads.
  const seedSelfOption = useMemo(() => {
    if (!currentEmployee) return undefined;
    return [{
      value: currentEmployee.id,
      label: `${currentEmployee.name || ''}${currentEmployee.code ? ` (${currentEmployee.code})` : ''}`.trim(),
    }];
  }, [currentEmployee]);

  // Year options: previous 4 years + current + next year
  const yearOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    const opts: { value: string; label: string }[] = [{ value: '', label: 'All years' }];
    for (let y = cy + 1; y >= cy - 4; y--) opts.push({ value: String(y), label: String(y) });
    return opts;
  }, []);

  // --- Base query params sent to /Reports/timesheet-drill on every level ---
  const baseQueryParams = useMemo(() => {
    const p: Record<string, any> = {
      // Forced WIP filters
      chargeTypeKey: 'C',
      requireEngagement: true,
      // Tells the backend which module's permissions to evaluate for visibility scope
      moduleContext: 'wip-report',
    };
    if (fromDate) p.fromDate = fromDate;
    if (toDate) p.toDate = toDate;
    if (managerId) p.managerId = managerId;
    if (partnerId) p.partnerId = partnerId;
    if (year) p.year = parseInt(year, 10);
    if (onlyOpen) p.onlyOpen = true;
    return p;
  }, [fromDate, toDate, managerId, partnerId, year, onlyOpen]);

  // --- Filter display items for the summary bar ---
  const baseFilterDisplay: FilterDisplayItem[] = useMemo(() => {
    const items: FilterDisplayItem[] = [];
    items.push({ label: 'Period', value: formatDateRange(fromDate, toDate) });
    items.push({ label: 'Manager', value: managerId ? 'Selected' : 'All' });
    items.push({ label: 'Partner', value: partnerId ? 'Selected' : 'All' });
    items.push({ label: 'Year', value: year || 'All' });
    items.push({ label: 'Status', value: onlyOpen ? 'Open only' : 'All' });
    return items;
  }, [fromDate, toDate, managerId, partnerId, year, onlyOpen]);

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (fromDate || toDate) c++;
    if (managerId) c++;
    if (partnerId) c++;
    if (year) c++;
    if (!onlyOpen) c++; // user explicitly turned the default OFF — counts as a filter override
    return c;
  }, [fromDate, toDate, managerId, partnerId, year, onlyOpen]);

  const handleResetFilters = useCallback(() => {
    setFromDate(fyStart());
    setToDate(today());
    setManagerId(currentEmployee?.id || '');
    setPartnerId('');
    setYear('');
    setOnlyOpen(true);
  }, [currentEmployee?.id]);

  // --- Filter panel JSX ---
  const filterPanel = filterOpen ? (
    <div className="search-panel">
      <div className="search-panel-header">
        <h3 className="search-panel-title">Filters</h3>
        <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close">
          <IconSystem.x size={14} />
        </button>
      </div>
      <div className="search-panel-content">
        <div className="filter-section">
          <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From Date</label>
          <input
            type="date" className="form-input"
            value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
          />
        </div>
        <div className="filter-section">
          <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To Date</label>
          <input
            type="date" className="form-input"
            value={toDate} onChange={(e) => setToDate(e.target.value)}
            style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
          />
        </div>
        <div className="filter-section">
          <SyutScopedEmployeeSelect
            label="Manager"
            scopeMode="view"
            moduleId="wip-report"
            role="manager"
            value={managerId || undefined}
            onChange={(val) => setManagerId(val || '')}
            seedOptions={seedSelfOption}
            isClearable
          />
        </div>
        <div className="filter-section">
          <SyutEmployeeSelect
            label="Partner"
            value={partnerId || undefined}
            role="partner"
            onChange={(val) => setPartnerId(val)}
            isClearable
          />
        </div>
        <div className="filter-section">
          <SyutSelect
            label="Year"
            options={yearOptions}
            value={year}
            onChange={(val) => setYear((val as string) || '')}
            isClearable={false}
            size="sm"
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPlacement="auto"
          />
        </div>
        <div className="filter-section">
          <label className="form-label" style={{ fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={onlyOpen}
              onChange={(e) => setOnlyOpen(e.target.checked)}
            />
            Show only open engagements
          </label>
        </div>
      </div>
      <div className="filter-actions">
        <button className="btn btn-secondary" onClick={handleResetFilters}>Clear</button>
        <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
      </div>
    </div>
  ) : null;

  return (
    <SyutDrillScreen
      title="WIP Report"
      description="Work-in-progress: chargeable hours × standard rate, drill from any dimension down to individual entries."
      icon={<IconSystem.dollarSign size={18} />}
      rootGroupBy="Client"
      defaultDrillPath={DEFAULT_DRILL_PATH}
      availableDimensions={ALL_DIMENSIONS}
      baseQueryParams={baseQueryParams}
      baseFilterDisplay={baseFilterDisplay}
      onResetBaseFilters={handleResetFilters}
      filterPanel={filterPanel}
      filterOpen={filterOpen}
      onToggleFilter={() => setFilterOpen(prev => !prev)}
      activeFilterCount={activeFilterCount}
      searchPlaceholder="Search client, engagement, employee..."
    />
  );
};

export default WipReportScreen;
